import { useMemo, useState, type ReactNode } from 'react'
import { AlertCircle, AlertTriangle, Building2, ChevronRight, DoorOpen, Loader2, Search, Warehouse } from 'lucide-react'
import type { ApiStorageNode, CapacitySummaryRow, StorageCondition, StorageConditionMeta } from '@/types/storage'
import { useFacilityLocations } from '@/hooks/useFacility'
import { useFreeSlots } from '@/hooks/useStorage'
import { useLocationText } from '@/hooks/useStoragePlaces'
import { usePermission } from '@/lib/permissions'
import { freePositions } from '@/lib/productStorage'
import { unitStatus } from '@/lib/storageList'
import { profileLabel } from '@/lib/storageTempMapping'
import {
  groupUnitsByPlace,
  isTemporaryUnit,
  placeOfUnit,
  searchUnits,
  unitUnavailableMessage,
  slotGroups,
  type PickerBuilding,
  type PickerRoom,
} from '@/lib/locationPicker'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

/** Slots one unit offers at a time; `freeTotal` still says how many there are. */
const SLOT_PAGE = 200

/**
 * Pick where something is stored, the way someone walks to it: building, then
 * room, then storage unit, then slot (z8nrz7fqfy).
 *
 * Only places that can take the item appear — `units` is already narrowed to
 * its condition and to units with room — and a search jumps straight to a unit
 * from any level. Slots come from the free-slots endpoint, one page per unit,
 * so a freezer of a million slots costs what a rack of ten does.
 *
 * `mode: 'unit'` stops at the storage unit, for the batch dialog, which
 * spreads a split's aliquots over consecutive slots itself.
 */
export function StorageLocationPicker({
  units,
  requires,
  conditions,
  mode = 'slot',
  initialUnitId = null,
  selectedSlotId = null,
  selectedUnitId = null,
  onSelectSlot,
  onSelectUnit,
}: {
  units: CapacitySummaryRow[]
  requires: StorageCondition | null
  conditions: StorageConditionMeta[]
  mode?: 'slot' | 'unit'
  initialUnitId?: string | null
  selectedSlotId?: string | null
  selectedUnitId?: string | null
  onSelectSlot?: (slot: ApiStorageNode) => void
  onSelectUnit?: (unit: CapacitySummaryRow) => void
}) {
  const { can } = usePermission()
  const locations = useFacilityLocations(can('equipment.read') || can('inventory.view'))
  const buildings = useMemo(() => groupUnitsByPlace(locations.data, units), [locations.data, units])

  // Where the picker starts: on the suggested unit when there is one, else at
  // the top — or inside the only building, when there is nothing to choose.
  const [at, setPlace] = useState<{ building: string | null; room: string | null; unit: string | null } | null>(null)
  const [query, setQuery] = useState('')
  /** Why the unit just clicked cannot be chosen; cleared by any other move. */
  const [notice, setNotice] = useState<{ unitId: string; message: string } | null>(null)
  const setAt = (next: { building: string | null; room: string | null; unit: string | null }) => {
    setNotice(null)
    setPlace(next)
  }
  const start = useMemo(() => {
    const place = initialUnitId ? placeOfUnit(buildings, initialUnitId) : null
    if (place) return { building: place.building.key, room: place.room.key, unit: mode === 'slot' ? initialUnitId : null }
    if (buildings.length === 1) return { building: buildings[0].key, room: null, unit: null }
    return { building: null, room: null, unit: null }
  }, [buildings, initialUnitId, mode])
  const current = at ?? start

  const building = buildings.find((b) => b.key === current.building) ?? null
  const room = building?.rooms.find((r) => r.key === current.room) ?? null
  const unit = room?.units.find((u) => u.nodeId === current.unit) ?? null
  const matches = useMemo(() => searchUnits(buildings, query), [buildings, query])

  function openUnit(target: CapacitySummaryRow, inBuilding: PickerBuilding, inRoom: PickerRoom) {
    // Shown, never chosen: the server refuses put-away into a unit that is out
    // of service or mid-maintenance, so say why here instead of at Confirm.
    const refusal = unitUnavailableMessage(target)
    if (refusal) {
      setNotice({ unitId: target.nodeId, message: refusal })
      return
    }
    setQuery('')
    if (mode === 'unit') {
      setAt({ building: inBuilding.key, room: inRoom.key, unit: null })
      onSelectUnit?.(target)
    } else {
      setAt({ building: inBuilding.key, room: inRoom.key, unit: target.nodeId })
    }
  }

  if (units.length === 0) {
    return (
      <p className='rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground'>
        {requires ? `No storage unit at ${profileLabel(requires, conditions)} has room.` : 'No storage unit has room.'}
      </p>
    )
  }

  return (
    <div className='space-y-3'>
      <div className='relative'>
        <Search className='pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder='Search a building, room or storage unit'
          aria-label='Search storage locations'
          className='h-9 pl-8'
        />
      </div>

      {!query && (
        <nav aria-label='Location' className='flex flex-wrap items-center gap-1 text-sm'>
          <Crumb onClick={() => setAt({ building: null, room: null, unit: null })} current={!building}>
            All buildings
          </Crumb>
          {building && (
            <>
              <ChevronRight className='h-3.5 w-3.5 text-muted-foreground/60' />
              <Crumb onClick={() => setAt({ building: building.key, room: null, unit: null })} current={!room}>
                {building.label}
              </Crumb>
            </>
          )}
          {building && room && (
            <>
              <ChevronRight className='h-3.5 w-3.5 text-muted-foreground/60' />
              <Crumb onClick={() => setAt({ building: building.key, room: room.key, unit: null })} current={!unit}>
                {room.label}
              </Crumb>
            </>
          )}
          {unit && (
            <>
              <ChevronRight className='h-3.5 w-3.5 text-muted-foreground/60' />
              <Crumb current>{unit.name || unit.code}</Crumb>
            </>
          )}
        </nav>
      )}

      {notice && (
        <p
          role='alert'
          className='flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900'
        >
          <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
          {notice.message}
        </p>
      )}

      <div className='max-h-[46vh] min-h-[12rem] overflow-y-auto pr-1'>
        {query ? (
          matches.length === 0 ? (
            <p className='py-8 text-center text-sm text-muted-foreground'>Nothing matches “{query.trim()}”.</p>
          ) : (
            <CardGrid>
              {matches.map((m) => (
                <UnitCard
                  key={m.unit.nodeId}
                  unit={m.unit}
                  where={[m.building.label, m.room.label].join(' › ')}
                  conditions={conditions}
                  selected={m.unit.nodeId === selectedUnitId}
                  onClick={() => openUnit(m.unit, m.building, m.room)}
                />
              ))}
            </CardGrid>
          )
        ) : !building ? (
          <CardGrid>
            {buildings.map((b) => (
              <PlaceCard
                key={b.key}
                icon={<Building2 className='h-4 w-4' />}
                title={b.label}
                detail={b.detail}
                meta={`${b.rooms.length} room${b.rooms.length === 1 ? '' : 's'} · ${b.unitCount} unit${b.unitCount === 1 ? '' : 's'}`}
                free={b.free}
                onClick={() => setAt({ building: b.key, room: b.rooms.length === 1 ? b.rooms[0].key : null, unit: null })}
              />
            ))}
          </CardGrid>
        ) : !room ? (
          <CardGrid>
            {building.rooms.map((r) => (
              <PlaceCard
                key={r.key}
                icon={<DoorOpen className='h-4 w-4' />}
                title={r.detail ? `${r.label} · ${r.detail}` : r.label}
                detail={r.floor}
                meta={`${r.units.length} storage unit${r.units.length === 1 ? '' : 's'}${r.outOfService ? ' · room out of service' : ''}`}
                warn={r.outOfService}
                free={r.free}
                onClick={() => setAt({ building: building.key, room: r.key, unit: null })}
              />
            ))}
          </CardGrid>
        ) : !unit ? (
          <CardGrid>
            {room.units.map((u) => (
              <UnitCard
                key={u.nodeId}
                unit={u}
                conditions={conditions}
                selected={u.nodeId === selectedUnitId}
                onClick={() => openUnit(u, building, room)}
              />
            ))}
          </CardGrid>
        ) : (
          <SlotList unit={unit} requires={requires} selectedSlotId={selectedSlotId} onSelect={onSelectSlot} />
        )}
      </div>
    </div>
  )
}

function Crumb({ children, onClick, current }: { children: ReactNode; onClick?: () => void; current?: boolean }) {
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={current}
      aria-current={current ? 'location' : undefined}
      className={cn(
        'max-w-[14rem] truncate rounded px-1.5 py-0.5',
        current ? 'font-semibold text-foreground' : 'text-primary hover:bg-primary/5 hover:underline',
      )}
    >
      {children}
    </button>
  )
}

function CardGrid({ children }: { children: ReactNode }) {
  return <div className='grid gap-2 sm:grid-cols-2'>{children}</div>
}

const cardClass =
  'group flex w-full items-start gap-3 rounded-xl border bg-card p-3 text-left transition-colors hover:border-primary/50 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

function FreeCount({ free }: { free: number }) {
  return (
    <span className='shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700'>
      {free.toLocaleString()} free
    </span>
  )
}

function PlaceCard({
  icon,
  title,
  detail,
  meta,
  free,
  warn = false,
  onClick,
}: {
  icon: ReactNode
  title: string
  detail: string | null
  meta: string
  free: number
  /** Out of service: still enterable, but marked in amber. */
  warn?: boolean
  onClick: () => void
}) {
  return (
    <button type='button' onClick={onClick} className={cn(cardClass, warn && 'border-amber-300 bg-amber-50/60')}>
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
          warn ? 'bg-amber-100 text-amber-700' : 'bg-primary/10 text-primary',
        )}
      >
        {icon}
      </span>
      <span className='min-w-0 flex-1'>
        <span className='flex items-start justify-between gap-2'>
          <span className='truncate font-medium'>{title}</span>
          <FreeCount free={free} />
        </span>
        {detail && <span className='block truncate text-xs text-muted-foreground'>{detail}</span>}
        <span className='block text-xs text-muted-foreground'>{meta}</span>
      </span>
      <ChevronRight className='mt-2.5 h-4 w-4 shrink-0 text-muted-foreground/50 group-hover:text-primary' />
    </button>
  )
}

function UnitCard({
  unit,
  where,
  conditions,
  selected,
  onClick,
}: {
  unit: CapacitySummaryRow
  where?: string
  conditions: StorageConditionMeta[]
  selected: boolean
  onClick: () => void
}) {
  const usedPct = unit.spaces > 0 ? Math.round((unit.used / unit.spaces) * 100) : 0
  const down = unit.unavailable ? unitStatus(unit).label : null
  return (
    <button
      type='button'
      onClick={onClick}
      // Clickable on purpose — the click says why it cannot be used — but
      // announced as unavailable rather than as a choice.
      aria-disabled={down ? true : undefined}
      title={down ? `${down}${unit.unavailable?.detail ? ` — ${unit.unavailable.detail}` : ''}` : undefined}
      className={cn(
        cardClass,
        down && 'cursor-not-allowed border-amber-300 bg-amber-50 hover:border-amber-400 hover:bg-amber-50',
        !down && selected && 'border-primary bg-primary/5 ring-1 ring-primary',
      )}
    >
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
          down ? 'bg-amber-100 text-amber-700' : 'bg-primary/10 text-primary',
        )}
      >
        {down ? <AlertTriangle className='h-4 w-4' /> : <Warehouse className='h-4 w-4' />}
      </span>
      <span className='min-w-0 flex-1 space-y-1'>
        <span className='flex items-start justify-between gap-2'>
          <span className='min-w-0'>
            <span className='block truncate font-medium'>{unit.name || unit.code}</span>
            <span className='block truncate text-xs text-muted-foreground'>
              {unit.name ? `${unit.code} · ` : ''}
              {unit.condition ? profileLabel(unit.condition, conditions) : 'No condition declared'}
            </span>
          </span>
          {down ? (
            <span className='shrink-0 rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900'>
              {down}
            </span>
          ) : isTemporaryUnit(unit) ? (
            <span className='shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800'>
              Temp
            </span>
          ) : (
            <FreeCount free={unit.remaining} />
          )}
        </span>
        {where && <span className='block truncate text-xs text-muted-foreground'>{where}</span>}
        <span className='flex items-center gap-2'>
          <span className='h-1.5 flex-1 overflow-hidden rounded-full bg-muted' aria-hidden>
            <span className='block h-full rounded-full bg-primary/60' style={{ width: `${usedPct}%` }} />
          </span>
          <span className='shrink-0 text-[11px] tabular-nums text-muted-foreground'>
            {unit.used.toLocaleString()} / {unit.spaces.toLocaleString()} used
          </span>
        </span>
      </span>
    </button>
  )
}

function SlotList({
  unit,
  requires,
  selectedSlotId,
  onSelect,
}: {
  unit: CapacitySummaryRow
  requires: StorageCondition | null
  selectedSlotId: string | null
  onSelect?: (slot: ApiStorageNode) => void
}) {
  const free = useFreeSlots(unit.nodeId, { condition: requires ?? undefined, limit: SLOT_PAGE })
  const locate = useLocationText()
  const groups = useMemo(() => {
    if (!free.data) return []
    // The server already applied the condition; this keeps the client's own
    // rule (active slots only) as every other picker does.
    const usable = new Set(freePositions(free.data.nodes, SLOT_PAGE, requires).map((n) => n.id))
    return slotGroups(free.data.nodes.filter((n) => usable.has(n.id) || n.kind !== 'POSITION'))
  }, [free.data, requires])

  if (free.isLoading) {
    return (
      <p className='flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' /> Finding free slots…
      </p>
    )
  }
  if (free.isError) {
    return (
      <p className='flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive'>
        <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' />
        Could not load this unit's slots: {(free.error as Error)?.message ?? 'unknown error'}
      </p>
    )
  }
  if (!free.data || groups.length === 0) {
    return (
      <p className='py-8 text-center text-sm text-muted-foreground'>
        {free.data?.declared === 0
          ? 'No slots are described in this unit yet. Declare its layout first, or pick another unit.'
          : 'This unit has no free slot that can take this item. Pick another unit.'}
      </p>
    )
  }

  const shown = groups.reduce((n, g) => n + g.slots.length, 0)
  return (
    <div className='space-y-3'>
      <p className='text-xs text-muted-foreground'>
        {free.data.freeTotal.toLocaleString()} free slot{free.data.freeTotal === 1 ? '' : 's'}
        {free.data.truncated && ` · showing the first ${shown.toLocaleString()}, in order`}
      </p>
      {groups.map((g) => (
        <section key={g.key} className='space-y-1.5'>
          {g.label && (
            <h4 className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>{g.label}</h4>
          )}
          <div className='grid grid-cols-[repeat(auto-fill,minmax(4.25rem,1fr))] gap-1.5'>
            {g.slots.map((slot) => {
              const selected = slot.id === selectedSlotId
              return (
                <button
                  key={slot.id}
                  type='button'
                  onClick={() => onSelect?.(slot)}
                  aria-pressed={selected}
                  title={locate(slot.path)}
                  className={cn(
                    'rounded-md border px-2 py-1.5 font-mono text-xs transition-colors',
                    selected
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'bg-background hover:border-primary/60 hover:bg-primary/5',
                  )}
                >
                  {slot.code}
                </button>
              )
            })}
          </div>
        </section>
      ))}
    </div>
  )
}
